import React, { useEffect } from 'react'
import { BrowserRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom'
import './scss/style.scss'
import { useDispatch, useSelector } from 'react-redux'
import { serviceUser } from './services/user.service'

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
)

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/pages/login/Login'))
const Register = React.lazy(() => import('./views/pages/register/Register'))
const SignUp = React.lazy(() => import('./views/pages/signup/SignUp'))
const PasswordRecovery = React.lazy(() =>
  import('./views/pages/passwordRecovery/PasswordRecovery'),
)
const PasswordRecoveryVerify = React.lazy(() =>
  import('./views/pages/passwordRecovery/PasswordRecoveryVerify'),
)
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'))
const OrganizationOnboarding = React.lazy(() =>
  import('./views/pages/organizationOnboarding/OrganizationOnboarding'),
)

const PASSWORD_RECOVERY_PATH = '/password_recovery'

// Paths reachable without a token
const UNAUTHENTICATED_PATHS = ['/login', '/register', '/signup', '/404', '/500']
// Prefixes reachable without a token, for paths carrying parameters
const UNAUTHENTICATED_PATH_PREFIXES = [PASSWORD_RECOVERY_PATH]

const isUnauthenticatedPath = (pathname) =>
  UNAUTHENTICATED_PATHS.includes(pathname) ||
  UNAUTHENTICATED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))

// Path reachable with a token but without an organization
const ONBOARDING_PATH = '/organization_onboarding'

// Rendered inside the Router so that it re-evaluates on every navigation.
const RouteGuards = () => {
  const token = useSelector((state) => state.token)
  const user = useSelector((state) => state.user)
  const location = useLocation()

  if (token == null) {
    return isUnauthenticatedPath(location.pathname) ? null : <Redirect to="/login" />
  }

  // Wait for the user to be loaded before deciding where to send it
  if (user == null) {
    return null
  }

  const hasOrganizations = user.organizations != null && user.organizations.length > 0
  if (!hasOrganizations && location.pathname !== ONBOARDING_PATH) {
    return <Redirect to={ONBOARDING_PATH} />
  }

  if (hasOrganizations && location.pathname === ONBOARDING_PATH) {
    return <Redirect to="/" />
  }

  return null
}

const App = () => {
  const dispatch = useDispatch()
  const token = useSelector((state) => state.token)

  useEffect(() => {
    syncUser()
  }, [token])

  async function syncUser() {
    if (token == null) {
      return
    }
    try {
      const localUser = JSON.parse(localStorage.getItem('user'))
      const userData = await serviceUser()

      if (userData.organizations == null) {
        // Organizations could not be fetched: keep the current state instead of
        // wrongly treating the user as organization-less.
        return
      }

      const organizations = userData.organizations
      if (organizations.length > 0) {
        const localSelectedOrganization = localUser?.selectedOrganization
        const matchingOrganization =
          localSelectedOrganization != null
            ? organizations.find((o) => o.id === localSelectedOrganization.id)
            : null
        userData.selectedOrganization = matchingOrganization ?? organizations[0]
      } else {
        userData.selectedOrganization = null
      }

      dispatch({ type: 'set', user: userData })
      localStorage.setItem('user', JSON.stringify(userData))
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <BrowserRouter>
      <React.Suspense fallback={loading}>
        <RouteGuards />
        <Switch>
          <Route exact path="/login" name="Login Page" render={(props) => <Login {...props} />} />
          <Route
            exact
            path="/register"
            name="Register Page"
            render={(props) => <Register {...props} />}
          />
          <Route exact path="/signup" name="Sign Up Page" render={(props) => <SignUp {...props} />} />
          <Route
            exact
            path={`${PASSWORD_RECOVERY_PATH}/verify/:code`}
            name="Password Recovery Verify"
            render={(props) => <PasswordRecoveryVerify {...props} />}
          />
          <Route
            exact
            path={PASSWORD_RECOVERY_PATH}
            name="Password Recovery"
            render={(props) => <PasswordRecovery {...props} />}
          />
          <Route exact path="/404" name="Page 404" render={(props) => <Page404 {...props} />} />
          <Route exact path="/500" name="Page 500" render={(props) => <Page500 {...props} />} />
          <Route
            exact
            path={ONBOARDING_PATH}
            name="Organization Onboarding"
            render={(props) => <OrganizationOnboarding {...props} />}
          />
          <Route path="/" name="Home" render={(props) => <DefaultLayout {...props} />} />
        </Switch>
      </React.Suspense>
    </BrowserRouter>
  )
}

export default App
